import React, { useState, useEffect } from 'react';
import { Award, Star, Gem, Crown, Sparkles, DollarSign, TrendingUp, Clock, CheckCircle, Info, Calendar, Activity, RefreshCw } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = '';

const EnhancedAIArbitrage = ({ onBalanceUpdate }) => {
  const { toast } = useToast();
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [investmentAmount, setInvestmentAmount] = useState('');
  const [activeInvestments, setActiveInvestments] = useState([]);
  const [completedInvestments, setCompletedInvestments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentView, setCurrentView] = useState('invest'); // 'invest', 'active', 'history'
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchPackages();
    fetchInvestments();
    fetchStats();
    
    // Refresh investments every 10 seconds
    const interval = setInterval(() => {
      fetchInvestments();
      fetchStats();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/arbitrage/packages`);
      if (response.data.success) {
        setPackages(Object.entries(response.data.packages).map(([id, data]) => ({
          id,
          ...data
        })));
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const fetchInvestments = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No auth token found');
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/arbitrage/my-investments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Investments response:', response.data);
      
      if (response.data.success) {
        const investments = response.data.investments || [];
        setActiveInvestments(investments.filter(inv => inv.status === 'active'));
        setCompletedInvestments(investments.filter(inv => inv.status === 'completed'));
        console.log('Active investments:', investments.filter(inv => inv.status === 'active').length);
        console.log('Completed investments:', investments.filter(inv => inv.status === 'completed').length);
      }
    } catch (error) {
      console.error('Error fetching investments:', error);
      if (error.response) {
        console.error('Response error:', error.response.status, error.response.data);
      }
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No auth token found for stats');
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/arbitrage/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Stats response:', response.data);
      
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (error.response) {
        console.error('Stats error:', error.response.status, error.response.data);
      }
    }
  };

  const handleInvest = async () => {
    if (!selectedPackage) {
      toast({ title: 'Error', description: 'Please select a package', variant: 'destructive' });
      return;
    }

    const amount = parseFloat(investmentAmount);
    if (!amount || amount < selectedPackage.min_investment) {
      toast({ 
        title: 'Invalid Amount', 
        description: `Minimum investment: $${selectedPackage.min_investment.toLocaleString()}`, 
        variant: 'destructive' 
      });
      return;
    }

    if (amount > selectedPackage.max_investment) {
      toast({ 
        title: 'Invalid Amount', 
        description: `Maximum investment: $${selectedPackage.max_investment.toLocaleString()}`, 
        variant: 'destructive' 
      });
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      const response = await axios.post(
        `${API_URL}/api/arbitrage/invest`,
        {
          package_id: selectedPackage.id,
          amount: amount
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Investment response:', response.data);
      
      if (response.data.success) {
        toast({
          title: '✅ Investment Created!',
          description: `Successfully invested $${amount.toLocaleString()} in ${selectedPackage.name} package`,
        });
        setInvestmentAmount('');
        setCurrentView('active');
        
        // Wait a moment then refresh data
        setTimeout(() => {
          fetchInvestments();
          fetchStats();
          if (onBalanceUpdate) onBalanceUpdate();
        }, 500);
      }
    } catch (error) {
      console.error('Investment error:', error);
      if (error.response) {
        console.error('Investment response error:', error.response.status, error.response.data);
      }
      toast({
        title: 'Investment Failed',
        description: error.response?.data?.detail || 'Failed to create investment',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const viewInvestmentDetail = async (investmentId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_URL}/api/arbitrage/investment/${investmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSelectedDetail(response.data.investment);
        setShowDetailModal(true);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load investment details',
        variant: 'destructive'
      });
    }
  };

  const packageIcons = {
    starter: TrendingUp,
    silver: Award,
    gold: Star,
    diamond: Gem,
    platinum: Sparkles,
    vip: Crown
  };

  const packageColors = {
    starter: 'from-green-400 to-emerald-600',
    silver: 'from-gray-400 to-gray-600',
    gold: 'from-yellow-400 to-amber-600',
    diamond: 'from-blue-400 to-purple-600',
    platinum: 'from-slate-300 to-slate-500',
    vip: 'from-orange-400 via-red-500 to-pink-600'
  };

  return (
    <div className="space-y-6">
      {/* Debug Info & Refresh Button */}
      <div className="flex items-center justify-between bg-slate-900/50 p-3 rounded-lg border border-purple-500/20">
        <div className="text-sm text-gray-400">
          Active: {activeInvestments.length} | Completed: {completedInvestments.length}
        </div>
        <button
          onClick={() => {
            fetchInvestments();
            fetchStats();
          }}
          className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-purple-400" />
              <span className="text-xs text-gray-400">Active</span>
            </div>
            <div className="text-2xl font-bold text-purple-400">{stats.active_investments}</div>
            <div className="text-xs text-gray-400 mt-1">${stats.total_active_value.toLocaleString()}</div>
          </div>

          <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-green-400" />
              <span className="text-xs text-gray-400">Total Profit</span>
            </div>
            <div className="text-2xl font-bold text-green-400">${stats.total_profit.toLocaleString()}</div>
            <div className="text-xs text-gray-400 mt-1">Avg ROI: {stats.average_roi}%</div>
          </div>

          <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4 text-purple-400" />
              <span className="text-xs text-gray-400">Total Invested</span>
            </div>
            <div className="text-2xl font-bold text-purple-400">${stats.total_invested.toLocaleString()}</div>
            <div className="text-xs text-gray-400 mt-1">{stats.total_investments} investments</div>
          </div>

          <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-xs text-gray-400">Success Rate</span>
            </div>
            <div className="text-2xl font-bold text-green-400">{stats.success_rate}%</div>
            <div className="text-xs text-gray-400 mt-1">{stats.completed_investments} completed</div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-2 bg-slate-900/50 p-2 rounded-xl border border-purple-500/20">
        {['invest', 'active', 'history'].map(view => (
          <button
            key={view}
            onClick={() => setCurrentView(view)}
            className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all ${
              currentView === view
                ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            {view === 'invest' ? 'Invest' : view === 'active' ? 'Active' : 'History'}
          </button>
        ))}
      </div>

      {/* Investment View */}
      {currentView === 'invest' && (
        <div className="space-y-4">
          {/* Package Selection */}
          <div>
            <h3 className="text-lg font-bold text-white mb-3">Select Investment Package</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {packages.map((pkg) => {
                const Icon = packageIcons[pkg.id] || Award;
                const color = packageColors[pkg.id] || 'from-gray-400 to-gray-600';
                
                return (
                  <button
                    key={pkg.id}
                    onClick={() => setSelectedPackage(pkg)}
                    className={`p-5 rounded-xl border-2 transition-all text-left ${
                      selectedPackage?.id === pkg.id
                        ? 'border-purple-400 bg-purple-500/10'
                        : 'border-slate-700 hover:border-purple-500/50 bg-slate-900/50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className={`w-6 h-6 bg-gradient-to-r ${color} bg-clip-text text-transparent`} style={{filter: 'drop-shadow(0 0 8px currentColor)'}} />
                      <span className={`font-bold text-lg bg-gradient-to-r ${color} bg-clip-text text-transparent`}>
                        {pkg.name}
                      </span>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between text-gray-400">
                        <span>Investment Range:</span>
                        <span className="text-white font-semibold">
                          ${(pkg.min_investment / 1000).toFixed(0)}K - ${(pkg.max_investment / 1000).toFixed(0)}K
                        </span>
                      </div>
                      <div className="flex justify-between text-gray-400">
                        <span>ROI:</span>
                        <span className="text-green-400 font-semibold">
                          {pkg.total_roi_min === pkg.total_roi_max 
                            ? `${pkg.total_roi_min}%`
                            : `${pkg.total_roi_min}% - ${pkg.total_roi_max}%`
                          }
                        </span>
                      </div>
                      <div className="flex justify-between text-gray-400">
                        <span>Duration:</span>
                        <span className="text-cyan-400 font-semibold">{pkg.period_days} days</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Amount Input */}
          {selectedPackage && (
            <div className="bg-slate-900/50 p-5 rounded-xl border border-slate-700">
              <h3 className="text-lg font-bold text-white mb-3">Enter Investment Amount</h3>
              
              <div className="mb-3">
                <input
                  type="number"
                  value={investmentAmount}
                  onChange={(e) => setInvestmentAmount(e.target.value)}
                  placeholder={`$${selectedPackage.min_investment.toLocaleString()} - $${selectedPackage.max_investment.toLocaleString()}`}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white text-lg font-bold focus:border-purple-500 focus:outline-none"
                />
              </div>

              <div className="flex gap-2 mb-4">
                {[5000, 50000, 250000, 1000000].map(amt => {
                  if (amt >= selectedPackage.min_investment && amt <= selectedPackage.max_investment) {
                    return (
                      <button
                        key={amt}
                        onClick={() => setInvestmentAmount(amt.toString())}
                        className="px-3 py-1 bg-slate-800 border border-slate-700 rounded text-sm hover:border-purple-500 transition-colors"
                      >
                        ${amt >= 1000000 ? (amt / 1000000) + 'M' : (amt / 1000) + 'K'}
                      </button>
                    );
                  }
                  return null;
                })}
              </div>

              {/* Investment Summary */}
              {investmentAmount && parseFloat(investmentAmount) >= selectedPackage.min_investment && (
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Investment:</span>
                    <span className="text-white font-bold">${parseFloat(investmentAmount).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Expected ROI:</span>
                    <span className="text-green-400 font-bold">
                      {selectedPackage.total_roi_min === selectedPackage.total_roi_max 
                        ? `${selectedPackage.total_roi_min}%`
                        : `${selectedPackage.total_roi_min}% - ${selectedPackage.total_roi_max}%`
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Potential Profit:</span>
                    <span className="text-green-400 font-bold">
                      {selectedPackage.total_roi_min === selectedPackage.total_roi_max 
                        ? `$${(parseFloat(investmentAmount) * selectedPackage.total_roi_min / 100).toLocaleString()}`
                        : `$${(parseFloat(investmentAmount) * selectedPackage.total_roi_min / 100).toLocaleString()} - $${(parseFloat(investmentAmount) * selectedPackage.total_roi_max / 100).toLocaleString()}`
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Duration:</span>
                    <span className="text-cyan-400 font-bold">{selectedPackage.period_days} days</span>
                  </div>
                </div>
              )}

              <button
                onClick={handleInvest}
                disabled={loading || !investmentAmount}
                className="w-full mt-4 py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white font-bold rounded-lg hover:from-purple-600 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : 'Invest Now'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Active Investments */}
      {currentView === 'active' && (
        <div className="space-y-3">
          <h3 className="text-lg font-bold text-white">Active Investments</h3>
          {activeInvestments.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <TrendingUp className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>No active investments</p>
            </div>
          ) : (
            activeInvestments.map(investment => (
              <div key={investment.investment_id} className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-bold text-white text-lg">{investment.package_name}</div>
                    <div className="text-sm text-gray-400">ID: {investment.investment_id}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-400">Current Returns</div>
                    <div className="text-xl font-bold text-green-400">
                      +${investment.total_returns?.toFixed(2) || '0.00'}
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-3 text-sm mb-3">
                  <div>
                    <div className="text-gray-400">Invested</div>
                    <div className="font-bold text-white">${investment.amount.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-gray-400">ROI</div>
                    <div className="font-bold text-cyan-400">{investment.profit_percentage}%</div>
                  </div>
                  <div>
                    <div className="text-gray-400">Total Value</div>
                    <div className="font-bold text-purple-400">
                      ${(investment.amount + (investment.total_returns || 0)).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-slate-700">
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Clock className="w-4 h-4" />
                    <span>Ends: {new Date(investment.end_date).toLocaleDateString()}</span>
                  </div>
                  <button
                    onClick={() => viewInvestmentDetail(investment.investment_id)}
                    className="flex items-center gap-2 px-3 py-1 bg-purple-500/10 border border-purple-500/30 rounded text-purple-400 hover:bg-purple-500/20 transition-colors text-sm"
                  >
                    <Info className="w-4 h-4" />
                    Details
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Completed Investments History */}
      {currentView === 'history' && (
        <div className="space-y-3">
          <h3 className="text-lg font-bold text-white">Investment History</h3>
          {completedInvestments.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>No completed investments</p>
            </div>
          ) : (
            completedInvestments.map(investment => (
              <div key={investment.investment_id} className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                {/* Profit Highlight at Top */}
                <div className="mb-3 pb-3 border-b border-slate-700">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-6 h-6 text-green-400" />
                    <span className="text-2xl font-bold text-green-400">
                      Profit +${investment.total_returns?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-400">
                    ROI: {investment.profit_percentage}% • Completed {new Date(investment.end_date).toLocaleDateString()}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-white">{investment.package_name}</div>
                    <div className="text-sm text-gray-400">
                      Investment: ${investment.amount.toLocaleString()} • 
                      Total Received: ${(investment.amount + (investment.total_returns || 0)).toLocaleString()}
                    </div>
                  </div>
                  <button
                    onClick={() => viewInvestmentDetail(investment.investment_id)}
                    className="flex items-center gap-2 px-3 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg text-cyan-400 hover:bg-cyan-500/20 transition-colors"
                  >
                    <Info className="w-4 h-4" />
                    Details
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedDetail && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl border border-purple-500/30 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Investment Details</h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>

              {/* Status Banner */}
              {selectedDetail.status === 'completed' && (
                <div className="p-4 rounded-lg mb-4 bg-green-500/20 border border-green-500/50">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-8 h-8 text-green-400" />
                    <div>
                      <div className="text-2xl font-bold text-green-400">
                        Profit +${selectedDetail.total_returns?.toFixed(2) || '0.00'}
                      </div>
                      <div className="text-sm text-green-300">Investment Completed Successfully</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Investment Information */}
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-gray-400">Investment ID</span>
                  <span className="font-mono text-white">{selectedDetail.investment_id}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-gray-400">Package</span>
                  <span className="font-bold text-white">{selectedDetail.package_name}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-gray-400">Investment Amount</span>
                  <span className="font-bold text-white">${selectedDetail.amount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-gray-400">Duration</span>
                  <span className="text-white">{selectedDetail.period_days} days</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-gray-400">ROI Percentage</span>
                  <span className="text-cyan-400 font-bold">{selectedDetail.profit_percentage}%</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-gray-400">Total Returns</span>
                  <span className="text-green-400 font-bold">
                    +${selectedDetail.total_returns?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-gray-400">Total Value</span>
                  <span className="text-purple-400 font-bold">
                    ${selectedDetail.total_value?.toLocaleString() || '0'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-gray-400">Start Date</span>
                  <span className="text-white">{new Date(selectedDetail.start_date).toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-gray-400">End Date</span>
                  <span className="text-white">{new Date(selectedDetail.end_date).toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-700">
                  <span className="text-gray-400">Status</span>
                  <span className={`font-bold ${
                    selectedDetail.status === 'active' ? 'text-yellow-400' : 'text-green-400'
                  }`}>
                    {selectedDetail.status.toUpperCase()}
                  </span>
                </div>
              </div>

              <button
                onClick={() => setShowDetailModal(false)}
                className="w-full mt-6 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedAIArbitrage;
